// Очередь действий без связи: отчёт прораба, отметки оператора. Каждый запрос несёт Idempotency-Key,
// поэтому повторная отправка при восстановлении связи не создаёт дублей.

import { Api, ApiError, uid } from "./api";

export type Queued = {
  id: string;
  method: "POST" | "PATCH";
  path: string;
  body: unknown;
  key: string;
  label: string;
  createdAt: string;
  error?: string;
};

type Listener = (items: Queued[]) => void;

export class OfflineQueue {
  private storeKey: string;
  private listeners = new Set<Listener>();
  private running = false;

  constructor(private api: Api, app: string) {
    this.storeKey = `rbs.${app}.queue`;
    window.addEventListener("online", () => this.flush());
    setInterval(() => this.flush(), 20_000);
  }

  get items(): Queued[] {
    try {
      return JSON.parse(localStorage.getItem(this.storeKey) || "[]");
    } catch {
      return [];
    }
  }

  private save(items: Queued[]): void {
    try {
      localStorage.setItem(this.storeKey, JSON.stringify(items));
    } catch {
      /* хранилище недоступно — очередь живёт только в памяти этой вкладки */
    }
    this.listeners.forEach((l) => l(items));
  }

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    l(this.items);
    return () => this.listeners.delete(l);
  }

  /** Отправить сразу; при обрыве связи — положить в очередь и вернуть null. */
  async send<T>(method: "POST" | "PATCH", path: string, body: unknown, label: string): Promise<T | null> {
    const key = uid();
    try {
      return await this.api.raw<T>(method, path, body, true, { "Idempotency-Key": key });
    } catch (e) {
      if (e instanceof ApiError && e.status === 0) {
        this.save([...this.items, { id: uid(), method, path, body, key, label, createdAt: new Date().toISOString() }]);
        return null;
      }
      throw e;
    }
  }

  async flush(): Promise<void> {
    if (this.running || !navigator.onLine) return;
    this.running = true;
    try {
      for (const item of this.items) {
        try {
          await this.api.raw(item.method, item.path, item.body, true, { "Idempotency-Key": item.key });
          this.save(this.items.filter((x) => x.id !== item.id));
        } catch (e) {
          if (e instanceof ApiError && e.status === 0) break;
          // сервер отклонил (например, данные устарели) — оставляем с текстом ошибки, чтобы человек решил
          this.save(this.items.map((x) => (x.id === item.id ? { ...x, error: (e as Error).message } : x)));
        }
      }
    } finally {
      this.running = false;
    }
  }

  remove(id: string): void {
    this.save(this.items.filter((x) => x.id !== id));
  }
}
